import { useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { GraduationCap, HeartHandshake, Users, Sparkles, ShieldCheck, ScanLine, Send, IndianRupee } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Reveal, FadeIn, MaskedLines } from "../components/Motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const CAUSES = [
  { icon: GraduationCap, name: "Educate a Child", desc: "Sponsor course fees for a deserving student" },
  { icon: Sparkles, name: "Teacher Scholarships", desc: "Fund NTT/PTT training for future educators" },
  { icon: Users, name: "Women Empowerment", desc: "Vocational skills for sustainable livelihoods" },
  { icon: HeartHandshake, name: "General Fund", desc: "Where the need is greatest" },
];

const AMOUNTS = [500, 1000, 2500, 5000];

export default function Donate() {
  const [settings, setSettings] = useState(null);
  const [summary, setSummary] = useState(null);
  const [cause, setCause] = useState("General Fund");
  const [amount, setAmount] = useState(1000);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [paid, setPaid] = useState(false);
  const [utr, setUtr] = useState("");
  const [honey, setHoney] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
    api.get("/donations/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const effectiveAmount = custom !== "" ? Number(custom) || 0 : amount;

  const upiLink = useMemo(() => {
    if (!settings?.upi_id) return "";
    const enc = encodeURIComponent;
    const payee = (settings.upi_name || "St Marys Charitable Trust").replace(/[^a-zA-Z0-9. ]/g, "");
    const note = `Donation ${cause}`.replace(/[^a-zA-Z0-9 ]/g, "");
    let link = `upi://pay?pa=${enc(settings.upi_id)}&pn=${enc(payee)}&cu=INR&tn=${enc(note)}`;
    if (effectiveAmount > 0) link += `&am=${effectiveAmount}`;
    return link;
  }, [settings, cause, effectiveAmount]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and mobile number.");
      return;
    }
    if (!paid || utr.trim().length < 8) {
      toast.error("Pay via the QR code first, then enter your UPI transaction (UTR) number.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/donations", {
        ...form,
        email: form.email || null,
        message: form.message || null,
        amount: effectiveAmount > 0 ? String(effectiveAmount) : "",
        cause,
        transaction_id: utr.trim(),
        website: honey,
      });
      toast.success("Thank you! Your paid pledge is recorded — we will confirm your transfer and arrange the 80G receipt.");
      setForm({ name: "", phone: "", email: "", message: "" });
      setPaid(false);
      setUtr("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="donate-page" className="pt-32 lg:pt-40">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 items-center gap-12 ${settings?.donation_image ? "lg:grid-cols-12" : ""}`}>
          <div className={settings?.donation_image ? "lg:col-span-7" : ""}>
            <FadeIn>
              <p className="inline-flex items-center gap-2 rounded-full border border-forest/15 bg-white/70 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-forest">
                <ShieldCheck className="h-3.5 w-3.5 text-terracotta" /> 80G Registered · Tax Benefits Available
              </p>
            </FadeIn>
            <h1 className="mt-6 font-serif text-5xl font-bold leading-[1.04] tracking-tight text-forest-deep sm:text-6xl lg:text-7xl" data-testid="donate-heading">
              <MaskedLines
                lines={[
                  "Give a little.",
                  <span key="l2" className="italic text-terracotta">Change</span>,
                  "a life entirely.",
                ]}
              />
            </h1>
            <FadeIn delay={0.6}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg">
                Every rupee goes to classrooms, scholarships and livelihoods in Mandya. Scan the QR, give in seconds,
                and we'll honour your kindness with an 80G tax receipt.
              </p>
            </FadeIn>
            {summary && summary.month_received > 0 && (
              <FadeIn delay={0.75}>
                <div data-testid="donation-counter" className="mt-8 inline-flex items-center gap-3 rounded-full border border-terracotta/30 bg-white px-6 py-3 shadow-sm">
                  <HeartHandshake className="h-5 w-5 text-terracotta" />
                  <p className="font-display text-sm font-semibold text-forest-deep">
                    ₹{summary.month_received.toLocaleString("en-IN")} raised this month
                    <span className="font-normal text-stone-500">
                      {" "}by {summary.month_donors} kind {summary.month_donors === 1 ? "heart" : "hearts"}
                    </span>
                  </p>
                </div>
              </FadeIn>
            )}
          </div>
          {settings?.donation_image && (
            <Reveal className="lg:col-span-5" delay={0.3}>
              <div className="overflow-hidden rounded-t-[12rem] rounded-b-[2rem] border-[10px] border-white shadow-2xl shadow-forest/15" data-testid="donation-hero-image">
                <img src={settings.donation_image} alt="St. Mary's Charitable Trust — your donation at work" className="h-80 w-full object-cover sm:h-[420px]" />
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">Step 1 · Choose a Cause</p>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="cause-options">
                {CAUSES.map((c) => (
                  <button
                    key={c.name}
                    data-testid={`cause-card-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setCause(c.name)}
                    className={`rounded-3xl border p-6 text-left transition-all duration-300 ${
                      cause === c.name
                        ? "border-terracotta bg-white shadow-xl shadow-terracotta/10"
                        : "border-forest/10 bg-white hover:border-forest/30"
                    }`}
                  >
                    <c.icon className={`h-6 w-6 ${cause === c.name ? "text-terracotta" : "text-forest"}`} />
                    <p className="mt-3 font-serif text-xl font-semibold text-forest-deep">{c.name}</p>
                    <p className="mt-1 text-sm text-stone-500">{c.desc}</p>
                  </button>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <p className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">Step 2 · Choose an Amount</p>
              <div className="mt-5 flex flex-wrap gap-3" data-testid="amount-options">
                {AMOUNTS.map((a) => (
                  <button
                    key={a}
                    data-testid={`amount-${a}`}
                    onClick={() => { setAmount(a); setCustom(""); }}
                    className={`flex items-center gap-1 rounded-full px-6 py-3 font-display text-sm font-semibold transition-all duration-300 ${
                      custom === "" && amount === a
                        ? "bg-forest text-cream shadow-lg shadow-forest/20"
                        : "border border-forest/20 bg-white text-forest hover:border-forest/50"
                    }`}
                  >
                    <IndianRupee className="h-4 w-4" />{a.toLocaleString("en-IN")}
                  </button>
                ))}
                <div className="flex items-center gap-2 rounded-full border border-forest/20 bg-white px-5 py-2.5">
                  <IndianRupee className="h-4 w-4 text-forest" />
                  <input
                    data-testid="custom-amount-input"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value.replace(/\D/g, "").slice(0, 7))}
                    placeholder="Custom"
                    inputMode="numeric"
                    className="w-24 bg-transparent font-display text-sm font-semibold text-forest outline-none placeholder:text-stone-400"
                  />
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <p className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">Step 3 · Tell Us Who You Are</p>
              <form onSubmit={submit} data-testid="donation-form" className="mt-5 rounded-3xl border border-forest/10 bg-white p-8">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="don-name">Name *</Label>
                    <Input id="don-name" data-testid="donation-name-input" value={form.name} onChange={set("name")} placeholder="Your full name" className="mt-2 h-11" />
                  </div>
                  <div>
                    <Label htmlFor="don-phone">Mobile *</Label>
                    <Input id="don-phone" data-testid="donation-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="mt-2 h-11" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="don-email">Email (for your 80G receipt)</Label>
                    <Input id="don-email" type="email" data-testid="donation-email-input" value={form.email} onChange={set("email")} placeholder="you@example.com" className="mt-2 h-11" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="don-message">A note with your gift (optional)</Label>
                    <Textarea id="don-message" data-testid="donation-message-input" value={form.message} onChange={set("message")} placeholder="In honour of... / For the children of..." className="mt-2 min-h-[90px]" />
                  </div>
                  <div className="rounded-2xl border border-terracotta/25 bg-terracotta/5 p-5 sm:col-span-2" data-testid="payment-gate">
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        data-testid="payment-confirm-checkbox"
                        checked={paid}
                        onChange={(e) => setPaid(e.target.checked)}
                        className="mt-1 h-4 w-4 shrink-0 accent-[#C86D3B]"
                      />
                      <span className="text-sm leading-relaxed text-stone-700">
                        <strong className="text-forest-deep">I have completed the payment</strong> using the QR code / UPI app above.
                        Your pledge can be sent only after payment.
                      </span>
                    </label>
                    {paid && (
                      <div className="mt-4">
                        <Label htmlFor="don-utr">UPI Transaction / UTR Number *</Label>
                        <Input
                          id="don-utr"
                          data-testid="donation-utr-input"
                          value={utr}
                          onChange={(e) => setUtr(e.target.value)}
                          placeholder="e.g. 432156789012"
                          className="mt-2 h-11 bg-white"
                        />
                        <p className="mt-1.5 text-xs text-stone-400">
                          Find it in your payment app under transaction details — this lets us confirm your gift instantly.
                        </p>
                      </div>
                    )}
                  </div>
                  <input type="text" name="website" value={honey} onChange={(e) => setHoney(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-testid="donation-honeypot" />
                </div>
                <Button type="submit" disabled={submitting || !paid || utr.trim().length < 8} data-testid="donation-submit-button" className="mt-6 w-full rounded-full bg-terracotta py-6 font-display text-base text-white hover:bg-terracotta-dark disabled:opacity-60">
                  {submitting ? "Recording..." : !paid ? "Pay via QR First to Enable" : (<>Send My Paid Pledge <Send className="ml-2 h-4 w-4" /></>)}
                </Button>
                <p className="mt-3 text-center text-xs text-stone-400">The form unlocks only after payment — every pledge we receive is a real gift.</p>
              </form>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.2} className="lg:sticky lg:top-28">
              <div className="grain-overlay relative overflow-hidden rounded-[2rem] bg-forest-deep p-8 text-cream sm:p-10" data-testid="donation-qr-card">
                <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-gold/15 blur-3xl" />
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Scan & Give</p>
                <h2 className="mt-3 font-serif text-3xl font-semibold">Your QR is ready.</h2>
                <p className="mt-2 text-sm text-cream/70">
                  {cause} {effectiveAmount > 0 && <>· <span className="font-semibold text-gold">₹{effectiveAmount.toLocaleString("en-IN")}</span></>}
                </p>

                <div className="mt-7 flex justify-center rounded-2xl bg-white p-6" data-testid="donation-qr">
                  {settings?.qr_image ? (
                    <img src={settings.qr_image} alt="Donation UPI QR code" className="h-52 w-52 object-contain" />
                  ) : upiLink ? (
                    <QRCode value={upiLink} size={208} fgColor="#112A20" />
                  ) : (
                    <div className="flex h-52 w-52 flex-col items-center justify-center text-center text-stone-400">
                      <ScanLine className="mb-3 h-8 w-8" />
                      <p className="px-4 text-xs leading-relaxed">Our donation QR is being set up.<br />Please call +91 79750 94721 to give.</p>
                    </div>
                  )}
                </div>

                {upiLink && (
                  <a
                    href={upiLink}
                    data-testid="upi-open-button"
                    className="mt-6 flex items-center justify-center gap-2 rounded-full bg-gold py-3.5 font-display text-sm font-semibold text-forest-deep transition-transform active:scale-[0.98]"
                  >
                    <ScanLine className="h-4 w-4" /> Open in UPI App (GPay / PhonePe / Paytm)
                  </a>
                )}

                <div className="mt-6 rounded-2xl border border-cream/15 bg-cream/5 p-5">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    <div className="text-xs leading-relaxed text-cream/75">
                      <p className="font-semibold text-gold">80G Registered — tax deduction on every donation</p>
                      <p className="mt-1.5">
                        Approval No: <span className="font-mono text-cream">ABNTS2788MF20261</span> · PAN: <span className="font-mono text-cream">ABNTS2788M</span>
                      </p>
                      <p className="mt-1">Valid for AY 2026-27 to 2028-29 · Issued by the Principal Commissioner of Income Tax</p>
                      <p className="mt-1.5">Share your email in the pledge form and we'll arrange your 80G receipt.</p>
                      <a
                        href="/80g-certificate.pdf"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="eighty-g-certificate-link"
                        className="mt-2.5 inline-flex items-center gap-1.5 font-semibold text-gold underline decoration-gold/40 underline-offset-4 hover:text-cream"
                      >
                        View our 80G Certificate
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8" data-testid="donation-impact">
        <Reveal>
          <div className="grid grid-cols-1 gap-6 rounded-[2.5rem] border border-forest/10 bg-white p-10 sm:grid-cols-3 sm:p-12">
            {[
              ["₹500", "covers study materials for one trainee teacher for a month"],
              ["₹2,500", "sponsors a full month of IELTS coaching for a rural student"],
              ["₹10,000", "funds a complete vocational livelihood course for one woman"],
            ].map(([amt, impact], i) => (
              <div key={amt} className="text-center sm:text-left" data-testid={`impact-${i}`}>
                <p className="font-serif text-4xl font-bold text-terracotta">{amt}</p>
                <p className="mt-2 text-sm leading-relaxed text-stone-600">{impact}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
